import * as Sentry from '@sentry/react';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import {
    resolveLocationToNavigationScreen,
    resolveNavigationScreenToHref,
} from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateErrorHeader,
    EmptyStateIcon,
    Heading,
} from '@gitbook/spine-react';
import { HTTPError, Logger, type SentryScope, SentryTags } from '@gitbook/util-debug';

import { AuthTokenUnavailableError } from '../../state/auth';
import { useHideSplashscreen } from '../Splashscreen';

const logger = Logger('app:crash');

enum CrashType {
    GENERIC = 'crash',
    NETWORK = 'network-crash',
}

/**
 * React error boundary to show a reload button when an error occured.
 */
export function ErrorScreen(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;

    const onError = React.useCallback((error: Error, componentStack: string, eventId: string) => {
        logger.emergency(
            'An error occured during rendering:\n\n',
            error,
            '\n\nThe error is located at:\n',
            componentStack
        );

        analytics.logEvent({
            type: 'crash',
            crashType: getCrashType(error),
            sentryEvent: eventId,
        });
    }, []);

    return (
        <Sentry.ErrorBoundary
            fallback={(args) => {
                // Call onError when fallback is rendered
                if (args.error instanceof Error) {
                    onError(args.error, args.componentStack, args.eventId);
                }
                return <ErrorScreenFallback {...args} />;
            }}
            beforeCapture={beforeCapture}
        >
            {children}
        </Sentry.ErrorBoundary>
    );
}

type ErrorScreenFallbackProps = {
    error: unknown;
    componentStack: string;
    eventId: string;
    resetError(): void;
};

function ErrorScreenFallback(props: ErrorScreenFallbackProps): React.ReactElement {
    const { eventId, error } = props;

    const handleReload = () => {
        window.location.reload();
    };

    // Hide splashscreen if it crashes during the first render
    useHideSplashscreen();

    const renderMessage = () => {
        switch (getCrashType(error)) {
            case CrashType.NETWORK:
                return <NetworkScreenMessage onReload={handleReload} {...props} />;
            default:
                return (
                    <GeneralScreenMessage
                        onShareFeedback={
                            eventId ? () => Sentry.showReportDialog({ eventId }) : null
                        }
                        onReload={handleReload}
                        {...props}
                    />
                );
        }
    };

    return (
        <div className="flex min-h-full w-full flex-col bg-base" data-testid="error.screen">
            {renderMessage()}
        </div>
    );
}

function GeneralScreenMessage(
    props: ErrorScreenFallbackProps & {
        onReload: () => void;
        onShareFeedback: (() => void) | null;
    }
): React.ReactElement {
    const { onReload, onShareFeedback, error, componentStack } = props;

    return (
        <EmptyState>
            <EmptyStateErrorHeader />
            <Description>
                Our team has been notified. If you'd like to help, you can describe what happened
                below.
            </Description>
            <EmptyStateActions>
                <Button label="Reload" kind="primary" size="medium" onPress={onReload} />
                <Button href={getHomeHref()} label="Back to home" kind="secondary" size="medium" />
                {onShareFeedback && (
                    <Button
                        label="Let us know what happened"
                        kind="secondary"
                        size="medium"
                        onPress={onShareFeedback}
                    />
                )}
            </EmptyStateActions>
            {process.env.NODE_ENV === 'development' ? (
                <div className="mt-4">
                    <div>These additional informations are shown in development only.</div>
                    {error instanceof HTTPError ? (
                        <div>HTTP request failed with status {error.statusCode}</div>
                    ) : null}
                    <pre>{error instanceof Error ? error.stack : JSON.stringify(error)}</pre>
                    {componentStack ? <pre>{componentStack}</pre> : null}
                </div>
            ) : null}
        </EmptyState>
    );
}

function NetworkScreenMessage(props: { onReload: () => void }): React.ReactElement {
    const { onReload } = props;

    const onAnchorButtonPress = () => {
        window.open('https://gitbook.com/docs/resources/firewall-safelist', '_blank');
    };

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.CloudOff />
            </EmptyStateIcon>
            <Heading>Connection lost</Heading>
            <Description>
                Your device might be offline, or GitBook might be experiencing issues.
            </Description>
            <EmptyStateActions>
                <Button
                    label="Reload"
                    kind="primary"
                    size="medium"
                    onPress={onReload}
                    className="mr-2"
                />
                <Button
                    kind="secondary"
                    label="Network troubleshooting"
                    size="medium"
                    leadingIcon={Icon.Link}
                    onPress={onAnchorButtonPress}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

/**
 * Annotate the sentry error to identify react crashes.
 */
function beforeCapture(scope: SentryScope, error: Error): void {
    switch (getCrashType(error)) {
        case CrashType.NETWORK:
            scope.setTag(SentryTags.ERROR_CATEGORY, 'network-crash');
            break;
        default:
            scope.setTag(SentryTags.ERROR_CATEGORY, 'react-crash');
    }
}

function getCrashType(error: unknown): CrashType {
    if (error instanceof AuthTokenUnavailableError) {
        return CrashType.NETWORK;
    }

    if (
        error instanceof Error &&
        error.name === 'FirebaseError' &&
        'code' in error &&
        error.code === 'unavailable'
    ) {
        return CrashType.NETWORK;
    }

    return CrashType.GENERIC;
}

function getHomeHref(): string {
    const currentScreen = resolveLocationToNavigationScreen(window.location);
    const organization = 'organization' in currentScreen ? currentScreen.organization : undefined;

    return resolveNavigationScreenToHref({
        type: 'home',
        organization,
    });
}
